import { Box, type BoxProps, Collapse, Stack, Typography } from '@mui/material';
import { createContext, forwardRef, type ReactNode, useContext } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import type { useCollapseOnScroll } from './use-collapse-on-scroll';
import { useSearchPageHeight } from './use-search-page-height';
import { PHONE_LANDSCAPE_MEDIA } from '../../theme-constants';

/**
 * The parts a `SearchPage` is laid out from: a fixed-height card whose header,
 * toolbar and footer stay put while only the list between them scrolls.
 *
 * `SearchPage` is built from them, and so are the pages that are laid out like
 * one without being a table over a single paginated query (the documents
 * browser, the processor animals workspace). They are parts rather than one
 * frame with slots because those pages each put something of their own between
 * the rows — tabs, a breadcrumb, a selection bar.
 */

type Sx = SxProps<Theme>;

/**
 * Merges a part's own styles with the caller's.
 * @param base - The part's styles
 * @param sx - The caller's, applied last
 * @returns An `sx` array
 */
const mergeSx = (base: Sx, sx?: Sx): Sx => [
  base,
  ...(Array.isArray(sx) ? sx : [sx]),
];

const PageScrollContext = createContext(false);

export interface SearchPageCardProps extends Omit<BoxProps, 'height'> {
  /**
   * Let the DOCUMENT scroll instead of the body. The card is then as tall as
   * its rows and the footer pins itself to the bottom of the screen. It is the
   * only kind of scroll that makes a phone browser tuck its own toolbar away,
   * which on a phone in landscape is a sixth of the screen.
   */
  pageScroll?: boolean;
  /**
   * The card's height. Defaults to the usable viewport, with the 8px gap above
   * the card that goes with it. Pass `'fill'` when the card sits in a parent
   * that already has a measured height, to take that instead.
   */
  height?: string | number | 'fill';
}

/** The card itself: a column that clips, so only its body scrolls. */
export const SearchPageCard = forwardRef<HTMLDivElement, SearchPageCardProps>(
  (
    { height = undefined, pageScroll = false, sx = undefined, ...rest },
    ref,
  ) => {
    const pageHeight = useSearchPageHeight();
    const size = height ?? pageHeight;
    return (
      <PageScrollContext.Provider value={pageScroll}>
        <Box
          ref={ref}
          {...rest}
          sx={mergeSx(
            {
              bgcolor: 'background.paper',
              // Square on a phone, where it runs edge to edge.
              borderRadius: { xs: 0, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              // Not clipped under page scroll: a clipping ancestor is what a
              // sticky part would stick to, instead of the screen.
              overflow: pageScroll ? 'visible' : 'hidden',
              ...(height === 'fill' && { flex: 1, minHeight: 0 }),
              // At least the screen under page scroll, so a short list still
              // has its footer at the bottom.
              ...(height !== 'fill' &&
                (pageScroll
                  ? { minHeight: size, mt: 1 }
                  : { height: size, mt: 1 })),
            },
            sx,
          )}
        />
      </PageScrollContext.Provider>
    );
  },
);
SearchPageCard.displayName = 'SearchPageCard';

export interface SearchPageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Slides the band away — see `useCollapseOnScroll`. */
  collapsed?: boolean;
}

/**
 * The tinted title band across the top of the card.
 *
 * On a phone the title and action buttons take a good part of the screen
 * before a single row is drawn, so the band slides away while the list is
 * scrolled down and returns on a pull past the top.
 * @param props - Component props
 * @param props.title - The page title
 * @param props.subtitle - A line under the title
 * @param props.actions - Buttons, at the right on a wide screen
 * @param props.collapsed - Whether the band is hidden
 * @returns The header band
 */
export function SearchPageHeader({
  title,
  subtitle = undefined,
  actions = undefined,
  collapsed = false,
}: SearchPageHeaderProps) {
  return (
    <Collapse in={!collapsed} sx={{ flexShrink: 0 }}>
      <Stack
        // Stacked on a phone. Side by side, the actions sat on a fixed row
        // with nothing to stop them overlapping the title: a two-line subtitle
        // and an "Add animals" button simply drew on top of each other.
        // `flexShrink: 0` on the actions keeps the button its own size once
        // there IS room for a row.
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={{ xs: 1.5, sm: 2 }}
        sx={{
          flexShrink: 0,
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
    </Collapse>
  );
}

/**
 * The row under the header that holds the search box and its controls.
 * @param props - Box props
 * @param props.sx - Extra styles
 * @returns The toolbar row
 */
export function SearchPageToolbar({ sx = undefined, ...rest }: BoxProps) {
  return (
    <Box
      {...rest}
      sx={mergeSx(
        {
          flexShrink: 0,
          px: 1,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          // Every pixel of height counts on a phone in landscape.
          [PHONE_LANDSCAPE_MEDIA]: { py: 0.75 },
        },
        sx,
      )}
    />
  );
}

export interface SearchPageBodyProps extends BoxProps {
  /** Wires the list's scroll to the header — pass `useCollapseOnScroll()`. */
  collapse?: ReturnType<typeof useCollapseOnScroll>;
}

/** The one part of the card that scrolls. */
export const SearchPageBody = forwardRef<HTMLDivElement, SearchPageBodyProps>(
  ({ collapse = undefined, sx = undefined, ...rest }, ref) => {
    const pageScroll = useContext(PageScrollContext);
    return (
      <Box
        ref={ref}
        {...(collapse && {
          onScroll: collapse.onScroll,
          onTouchStart: collapse.onTouchStart,
          onTouchMove: collapse.onTouchMove,
          onWheel: collapse.onWheel,
        })}
        {...rest}
        sx={mergeSx(
          {
            flex: 1,
            minHeight: 0,
            overflow: pageScroll ? 'visible' : 'auto',
          },
          sx,
        )}
      />
    );
  },
);
SearchPageBody.displayName = 'SearchPageBody';

export interface SearchPageFooterProps {
  /** What is being counted, at the left: "42 results". */
  label: ReactNode;
  /** The pager, and anything that goes with it, at the right. */
  children?: ReactNode;
  sx?: Sx;
}

/** The count and pager, fixed under the list. */
export const SearchPageFooter = forwardRef<
  HTMLDivElement,
  SearchPageFooterProps
>(({ label, children = undefined, sx = undefined }, ref) => {
  const pinned = useContext(PageScrollContext);
  return (
    <Stack
      ref={ref}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={mergeSx(
        {
          flexShrink: 0,
          px: { xs: 1.5, sm: 3 },
          // The Pagination component carries its own `my: 2`, so on a phone
          // this row was ~32px taller than the controls in it.
          py: { xs: 0.5, sm: 2 },
          borderTop: 1,
          borderColor: 'divider',
          // Under page scroll nothing holds the footer in view, so it pins
          // itself to the bottom of the screen — clear of the home indicator,
          // which is only there once the browser's toolbar has tucked away.
          ...(pinned && {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            pb: 'calc(4px + env(safe-area-inset-bottom, 0px))',
          }),
        },
        sx,
      )}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ flexShrink: 0 }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
});
SearchPageFooter.displayName = 'SearchPageFooter';
